'use client';

import Link from 'next/link';
import Image from 'next/image';
import { ChevronDown, User } from 'lucide-react';
import { clsx } from 'clsx';
import { useCurrentUser, useAdmin } from '@paeffgen/react/crm-documents/feature-auth';
import { getContextIdentity } from './context-identity';

interface MobileHeaderProps {
  onPillClick: () => void;
  bannerVisible: boolean;
}

export function MobileHeader({ onPillClick, bannerVisible }: MobileHeaderProps) {
  const { user } = useCurrentUser();
  const { isAdmin, allCustomers, selectedCustomers } = useAdmin();

  const identity = getContextIdentity({ user, isAdmin, allCustomers, selectedCustomers });
  const avatarContent: React.ReactNode =
    identity.avatar.type === 'initials'
      ? identity.avatar.value
      : identity.avatar.type === 'count'
        ? identity.avatar.value
        : <User size={14} strokeWidth={2} />;
  const pillName = identity.primaryName;
  const avatarBg = identity.avatarBg;

  return (
    <header
      className={clsx(
        'md:hidden sticky z-40 bg-white border-b border-zinc-200 h-14 px-3 flex items-center gap-3',
        bannerVisible ? 'top-10' : 'top-0',
      )}
    >
      <Link href="/" className="shrink-0 flex items-center" aria-label="Startseite">
        <Image
          src="/logo-dachbaustoffe_color.svg"
          alt="Paeffgen Dachbaustoffe"
          width={140}
          height={28}
          className="h-7 w-auto object-contain dark:hidden"
          priority
        />
        <Image
          src="/logo-dachbaustoffe_dark.svg"
          alt="Paeffgen Dachbaustoffe"
          width={140}
          height={28}
          className="hidden h-7 w-auto object-contain dark:block"
          priority
        />
      </Link>

      <div className="flex-1" />

      <button
        type="button"
        onClick={onPillClick}
        className="inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-zinc-200 bg-white hover:border-zinc-300 active:bg-zinc-50 transition-colors min-w-0 max-w-[200px]"
        aria-label="Kontomenü öffnen"
      >
        <span
          className={clsx(
            'relative w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0',
            avatarBg,
          )}
        >
          {avatarContent}
          {isAdmin && (
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-white" />
          )}
        </span>
        <span className="text-[13px] font-semibold text-zinc-800 truncate flex-1 min-w-0">
          {pillName}
        </span>
        <ChevronDown size={14} className="text-zinc-400 shrink-0" />
      </button>
    </header>
  );
}
